import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { backgroundAnimation, positionAnimation, PositionAnimationState } from '../../../../core/consts/animations';
import { HeaderStateService } from './header-state.service';
import { IconeDirective } from '../../../directives/icone.directive';
import { FichaTecnicaDirective } from '../../../directives/ficha-tecnica.directive';
import { MenuFlutuanteComponent } from './menu-flutuante/menu-flutuante.component';
import { OPCOES_MENU_OUTRAS_CONSULTAS } from '../../../../core/consts/menu-outras-consultas';
import { mediaAte } from '../../../../core/consts/breakpoints';

type MenuHeader = 'outrasConsultas';

const EVENTOS_ROLAGEM_DO_USUARIO = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    animations: [backgroundAnimation(), positionAnimation('top', 0)],
    imports: [RouterLink, RouterLinkActive, IconeDirective, FichaTecnicaDirective, MenuFlutuanteComponent],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly headerStateService = inject(HeaderStateService);
    private readonly zone = inject(NgZone);

    private readonly ALTURA_VISIVEL_AO_OCULTAR: number = 12;

    private readonly MOUSE_ZONE_TO_SHOW_HEADER: number = 40;

    private readonly JANELA_ROLAGEM_DO_USUARIO_MS: number = 800;

    private interacaoDoUsuarioEm: number = -Infinity;

    private botaoPressionado: boolean = false;

    private onDestroy$: Subject<void> = new Subject<void>();

    private lastScrollY: number = 0;

    public headerPosition: 'absolute' | 'fixed' = 'absolute';

    public positionAnimationState: PositionAnimationState = PositionAnimationState.NoAnimation;

    public backgroundState: 'default' | 'white' = 'default';

    public deslocamentoAoOcultar: number = 0;

    public isPaineisAnaliticosRoute: boolean = false;

    public menuAberto: MenuHeader | null = null;

    public readonly opcoesOutrasConsultas = OPCOES_MENU_OUTRAS_CONSULTAS;

    protected readonly mediaMobile = mediaAte('mobile');

    @ViewChild('headerElement', { static: true })
    public header: ElementRef<HTMLElement> | undefined;

    public ngOnInit(): void {
        this.listenToRouteChange();
        this.zone.runOutsideAngular(() => {
            EVENTOS_ROLAGEM_DO_USUARIO.forEach((evento) =>
                window.addEventListener(evento, this.registrarInteracao, { passive: true, capture: true })
            );
            window.addEventListener('pointerup', this.soltarBotao, { passive: true, capture: true });
        });
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
        EVENTOS_ROLAGEM_DO_USUARIO.forEach((evento) =>
            window.removeEventListener(evento, this.registrarInteracao, { capture: true })
        );
        window.removeEventListener('pointerup', this.soltarBotao, { capture: true });
    }

    private readonly registrarInteracao = (evento: Event): void => {
        this.interacaoDoUsuarioEm = performance.now();
        if (evento.type === 'pointerdown') this.botaoPressionado = true;
    };

    private readonly soltarBotao = (): void => {
        this.botaoPressionado = false;
        this.interacaoDoUsuarioEm = performance.now();
    };

    private rolagemDoUsuario(): boolean {
        return (
            this.botaoPressionado || performance.now() - this.interacaoDoUsuarioEm < this.JANELA_ROLAGEM_DO_USUARIO_MS
        );
    }

    public abrirMenu(menu: MenuHeader): void {
        this.menuAberto = menu;
    }

    public alternarMenu(menu: MenuHeader): void {
        this.menuAberto = this.menuAberto === menu ? null : menu;
    }

    public fecharMenu(): void {
        this.menuAberto = null;
    }

    public fecharMenuSeFocoSaiu(event: FocusEvent): void {
        const container = event.currentTarget as HTMLElement;
        if (event.relatedTarget instanceof Node && !container.contains(event.relatedTarget)) {
            this.fecharMenu();
        }
    }

    @HostListener('document:keydown.escape')
    public onEscape(): void {
        if (this.menuAberto === null) return;
        this.fecharMenu();
    }

    public navigateToHome(): void {
        this.router.navigate(['/']);
    }

    @HostListener('window:scroll', ['$event'])
    public onWindowScroll(): void {
        const scrollY = window.scrollY;
        const alturaParaOcultar = this.alturaParaOcultar();
        this.deslocamentoAoOcultar = -alturaParaOcultar;
        this.handleHeaderBehavior(scrollY, alturaParaOcultar);
        this.lastScrollY = scrollY;
    }

    @HostListener('window:mousemove', ['$event'])
    public onMouseMove($event: MouseEvent): void {
        if (
            this.headerPosition === 'fixed' &&
            this.positionAnimationState === PositionAnimationState.Moved &&
            $event.clientY < this.MOUSE_ZONE_TO_SHOW_HEADER
        ) {
            this.showHeader();
        }
    }

    public get zIndex(): number {
        return this.headerPosition === 'fixed' ? 2 : 0;
    }

    private alturaParaOcultar(): number {
        return (this.header?.nativeElement.offsetHeight ?? 0) - this.ALTURA_VISIVEL_AO_OCULTAR;
    }

    private handleHeaderBehavior(scrollY: number, alturaParaOcultar: number): void {
        if (this.headerPosition === 'absolute') {
            this.validateAbsoluteHeaderBehavior(scrollY, alturaParaOcultar);
        } else {
            this.validateFixedHeaderBehavior(scrollY, alturaParaOcultar);
        }
    }

    private validateAbsoluteHeaderBehavior(scrollY: number, alturaParaOcultar: number): void {
        if (scrollY > alturaParaOcultar) {
            this.setHeaderInitialFixedBehavior();
        }
    }

    private validateFixedHeaderBehavior(scrollY: number, alturaParaOcultar: number): void {
        if (scrollY < alturaParaOcultar) {
            this.setHeaderAbsoluteBehavior();
        } else if (this.rolagemDoUsuario()) {
            if (scrollY > this.lastScrollY) {
                this.hideHeader();
            } else {
                this.showHeader();
            }
        }
    }

    private hideHeader(): void {
        this.fecharMenu();
        this.positionAnimationState = PositionAnimationState.Moved;
        this.headerStateService.setDisplayedAbsoluteHeader(false);
    }

    private showHeader(): void {
        this.positionAnimationState = PositionAnimationState.Default;
        this.headerStateService.setDisplayedAbsoluteHeader(true);
    }

    private setHeaderInitialFixedBehavior(): void {
        this.fecharMenu();
        this.positionAnimationState = PositionAnimationState.Moved;
        this.headerPosition = 'fixed';
        this.backgroundState = 'white';
    }

    private setHeaderAbsoluteBehavior(): void {
        this.positionAnimationState = PositionAnimationState.NoAnimation;
        this.headerPosition = 'absolute';
        this.backgroundState = 'default';
        this.headerStateService.setDisplayedAbsoluteHeader(false);
    }

    private listenToRouteChange(): void {
        this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.checkIfRouteIsPaineisAnaliticos(event.url);
                this.fecharMenu();
            }
        });
    }

    private checkIfRouteIsPaineisAnaliticos(url: string): void {
        this.isPaineisAnaliticosRoute = url.startsWith('/paineis-analiticos');
    }
}
